import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { translate } from '@/i18n/locale';

interface InfiniteSentinelProps {
  /** Load the next page. */
  onReach: () => void;
  /** There is another page to load. */
  hasMore: boolean;
  /** A fetch is already in flight. */
  loading: boolean;
  /** The last page failed — show the error footer instead of observing. */
  error?: boolean;
  /** Retry the failed page load. */
  onRetry?: () => void;
  /** Accessible label for the loading row. */
  label?: string;
}

/**
 * A viewport sentinel that calls `onReach` when it scrolls into view — pages
 * load as the user reaches the end of a grid or list, no button. When the last
 * page fails (`error`), it stops observing and renders a `role="alert"` footer
 * ("Couldn't load more." + a Retry button). Falls back to doing nothing where
 * IntersectionObserver is unavailable.
 */
export function InfiniteSentinel({
  onReach,
  hasMore,
  loading,
  error = false,
  onRetry,
  label = translate('common.loadingMore'),
}: InfiniteSentinelProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const onReachRef = useRef(onReach);
  onReachRef.current = onReach;

  useEffect(() => {
    const el = ref.current;
    if (!el || !hasMore || error || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onReachRef.current();
      },
      { rootMargin: '600px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, error]);

  if (error && !loading) {
    return (
      <div role="alert" className="flex flex-col items-center gap-2 py-6">
        <p className="text-caption text-ink-secondary">{translate('common.couldntLoadMore')}</p>
        {onRetry ? (
          <Button size="sm" variant="ghost" onClick={onRetry}>
            {translate('common.retry')}
          </Button>
        ) : null}
      </div>
    );
  }

  if (!hasMore) return null;

  return (
    <div ref={ref} role="status" aria-label={label} className="flex justify-center py-6">
      {loading ? <Loader2 className="size-5 animate-spin text-ink-tertiary" aria-hidden /> : null}
    </div>
  );
}

/** The subset of a TanStack Query infinite result the sentinel needs. */
export interface InfiniteQueryLike {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  fetchNextPage: () => unknown;
}

/**
 * The adapter from a TanStack Query infinite result to `InfiniteSentinel` — the
 * one implementation the hand-rolled `LoadMore`s (Friends, Notifications,
 * social/shared) duplicate. Screen agents should pass their query straight to
 * this instead of re-implementing the sentinel + loading/error footer.
 */
export function QueryLoadMore({ query, label }: { query: InfiniteQueryLike; label?: string }) {
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = query;
  return (
    <InfiniteSentinel
      onReach={() => void fetchNextPage()}
      hasMore={hasNextPage}
      loading={isFetchingNextPage}
      error={isError}
      onRetry={() => void fetchNextPage()}
      label={label}
    />
  );
}
